'use client';

import { useCallback } from 'react';
import { useApi } from './use-api';
import { useBusy } from './use-busy';
import { CreateUserDto, User, UserListParams, } from '@/schemas/user.schema';
import { PaginatedResponse } from '@/schemas/organization.schema';

export function useUserApi() {
    const { get, post, patch, del } = useApi();

    const [isLoading, whileLoading] = useBusy();
    // ponytail: create/update/remove/resendInvite deliberately share isCreating —
    // consumers bind one "saving" flag per hook instance (see user.modal Save button).
    const [isCreating, whileCreating] = useBusy();

    const getList = useCallback(
        (params?: UserListParams, signal?: AbortSignal) =>
            whileLoading(() => get<PaginatedResponse<User>>('/users', params, signal)),
        [get, whileLoading]
    );

    const getById = useCallback(
        (id: string) => whileLoading(() => get<User>(`/users/${id}`)),
        [get, whileLoading]
    );

    const create = useCallback(
        (data: CreateUserDto) => whileCreating(() => post<User>('/users', data)),
        [post, whileCreating]
    );

    const update = useCallback(
        (id: string, data: Partial<CreateUserDto>) => whileCreating(() => patch<User>(`/users/${id}`, data)),
        [patch, whileCreating]
    );

    const remove = useCallback(
        (id: string) => whileCreating(() => del<User>(`/users/${id}`)),
        [del, whileCreating]
    );

    const resendInvite = useCallback(
        (payload: { id?: string; email?: string; organizationId?: string }) =>
            whileCreating(() => {
                if (payload.id) {
                    return post<{
                        user: User;
                        inviteEmailSent: boolean;
                        warning?: string;
                        message?: string;
                    }>(`/users/${payload.id}/resend-invite`, {});
                }

                return post<{
                    user: User;
                    inviteEmailSent: boolean;
                    warning?: string;
                    message?: string;
                }>('/users/resend-invite', payload);
            }),
        [post, whileCreating]
    );

    return {
        getList,
        getById,
        create,
        update,
        remove,
        resendInvite,
        isLoading,
        isCreating,
    };
}
